import React, {
    useCallback,
    useState,
} from 'react';
import PropTypes from 'prop-types';
import Raven from 'raven-js';

import { formatIntervals, formatDeliveryDateMobile } from 'app/utils/date';
import {
    MAX_BELTWAY_DISTANCE,
    MAX_BELTWAY_DISTANCE_FOR_INSIDE_INTERVALS,
} from 'app/const/address';
import { SPB_CODES } from 'app/const/location';

import { Mobile, Desktop } from 'app/components/Responsive';
import {
    DateAndTimeSelectDesktop,
    DeliveryDateMobile2,
} from 'app/components/DeliveryDate';
import {
    MobileTimeSelectContainer,
    MobileDateSelectContainer,
} from 'app/components/Select';


const cityTitles = {
    msk: {
        inside: 'Москва',
        outside: 'За МКАД',
    },
    spb: {
        inside: 'Санкт-Петербург',
        outside: 'За КАД',
    },
};

const INSIDE_MKAD = 'inside_mkad';


const resolveCityByAddress = (address) => {
    try {
        const { data: { region_iso_code: subdivision } } = address;
        if (SPB_CODES.includes(subdivision)) {
            return 'spb';
        }
        return 'msk';
    } catch (error) {
        Raven.captureException(error);
        return 'msk';
    }
};


export default function DeliveryDateTime(props) {
    const {
        deliveryDates,
        date,
        interval: {
            index: timeIndex,
            beltwayHit: propTimePeriod,
        },
        deliveryIntervals,
        address,

        onSelectDate,
        onSelectTimeInterval,
    } = props;

    const city = resolveCityByAddress(address);
    const beltwayDistance = Number(address.data.beltway_distance);
    // TODO: ужасный быстрофикс, который в выходные меняет для замкада-12 км mkad_outside на mkad_inside
    // для отображения всех доставок
    const { weekday } = formatDeliveryDateMobile(date, 'ru');
    const isWeekend = weekday === 'сб' || weekday === 'вс';
    const isLessThan12kmFromMkad = beltwayDistance < MAX_BELTWAY_DISTANCE_FOR_INSIDE_INTERVALS;
    const timePeriod = isLessThan12kmFromMkad && isWeekend ? INSIDE_MKAD : propTimePeriod;

    const [dateSelectIsOpen, setDateSelectIsOpen] = useState(false);

    const toggleDateSelect = useCallback(() => {
        setDateSelectIsOpen((current) => !current);
    }, []);

    const [intervalSelectIsOpen, setIntervalSelectIsOpen] = useState(false);
    const toggleIntervalSelect = () => {
        setIntervalSelectIsOpen(!intervalSelectIsOpen);
    };

    const handleConfirm = (onConfirm) => onConfirm();

    const handleSelectDate = useCallback((nextDate) => {
        onSelectDate(nextDate);
    }, [onSelectDate]);

    const handleSelectTimeInterval = (beltwayHit, index) => {
        onSelectTimeInterval({ beltwayHit, index });
    };


    const formattedAvailableIntervals = formatIntervals(deliveryIntervals);
    const mkad = timePeriod === 'inside_mkad' ? 'inside' : 'outside';

    const isDateDisabled = (address.value === '' || address.value === null)
        || ((address.value !== '') && address.validation.hasError)
        || !address.data.house;

    // TODO: перенести в стор
    const isDeliveryOutsideZone = beltwayDistance > MAX_BELTWAY_DISTANCE;
    const isTimeChangedAnimation = false;
    const handleEndTimeAnimation = () => {};

    const insideBetlwayTitle = cityTitles[city].inside;
    const outsideBetlwayTitle = cityTitles[city].outside;
    const periodEndDate = '2021-01-03';

    const mobileDatesInfo = {
        isPromoPage: true,
        dates: deliveryDates,
        selectDay: date,
        onSelectDay: handleSelectDate,
        periodEndDate,

        /* используется в качестве флага, что не нужно показывать текст "с этой даты - другое меню" */
        isTrial: true,
    };

    const mobileTimeIntervalsInfo = {
        timeIntervals: formattedAvailableIntervals,
        mkad,
        selectTime: {
            timePeriod,
            timeIndex,
        },
        onSelectTime: handleSelectTimeInterval,
    };

    return (
        <>
            <Mobile isWideMobile>
                <DeliveryDateMobile2
                    applyNYStyles
                    timeIntervals={formattedAvailableIntervals}
                    selectDay={date}
                    selectTime={{
                        timePeriod,
                        timeIndex,
                    }}
                    mkad={mkad}
                    disabled={isDateDisabled}
                    isDeliveryOutsideZone={isDeliveryOutsideZone}
                    isLessThan12kmFromMkad={isLessThan12kmFromMkad}
                    onClickDates={toggleDateSelect}
                    onClickTimeIntervals={toggleIntervalSelect}

                    isDayChangedAnimation={false}

                    isTimeChangedAnimation={isTimeChangedAnimation}
                    onEndTimeAnimation={handleEndTimeAnimation}
                />

                <MobileDateSelectContainer
                    isSelectOpen={dateSelectIsOpen}
                    info={mobileDatesInfo}
                    onClose={toggleDateSelect}
                />
                <MobileTimeSelectContainer
                    isSelectRequired={intervalSelectIsOpen}
                    info={mobileTimeIntervalsInfo}
                    onClose={toggleIntervalSelect}
                />
            </Mobile>

            <Desktop isWideMobile>
                <DateAndTimeSelectDesktop
                    dates={deliveryDates}
                    timeIntervals={formattedAvailableIntervals}
                    selectDay={date}
                    selectTime={{
                        timePeriod,
                        timeIndex,
                    }}
                    mkad={mkad}
                    disabled={isDateDisabled}
                    isDeliveryOutsideZone={isDeliveryOutsideZone}
                    insideBetlwayTitle={insideBetlwayTitle}
                    outsideBetlwayTitle={outsideBetlwayTitle}
                    onToggleDay={() => {}}

                    /* используется в качестве флага, что не нужно показывать текст "с этой даты - другое меню" */
                    isTrial

                    periodEndDate={periodEndDate}
                    onToggleTime={() => {}}
                    onSelectDay={handleSelectDate}
                    onSelectTime={handleSelectTimeInterval}
                    isTimeChange={false}
                    dayToggleRef={null}
                    onOpenConfirm={handleConfirm}
                    onChangeDateConfirm={() => {}}
                    isDayChangedAnimation={false}
                    isTimeChangedAnimation={isTimeChangedAnimation}
                    onEndDayAnimation={() => {}}
                    onEndTimeAnimation={() => {}}
                />
            </Desktop>
        </>
    );
}

DeliveryDateTime.propTypes = {
    date: PropTypes.string.isRequired,
    interval: PropTypes.shape({
        index: PropTypes.number.isRequired,
        beltwayHit: PropTypes.string.isRequired,
    }).isRequired,
    deliveryDates: PropTypes.arrayOf(
        PropTypes.shape({}),
    ).isRequired,
    deliveryIntervals: PropTypes.arrayOf(
        PropTypes.shape({}),
    ).isRequired,
    address: PropTypes.shape({}).isRequired,

    onSelectDate: PropTypes.func.isRequired,
    onSelectTimeInterval: PropTypes.func.isRequired,
};
